import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './FloatingChat.css';

const WHATSAPP_LINK = 'https://wa.me/5493413047536?text=Hola%2C%20me%20contacto%20desde%20meyle.click';

const FloatingChat = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [formData, setFormData] = useState({ nombre: '', empresa: '', email: '', consulta: '' });
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (step === 'nombre' || step === 'empresa' || step === 'email' || step === 'consulta') {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [step]);

  const addBotMessage = (text, options = null) => {
    setMessages(prev => [...prev, { sender: 'bot', text, options, timestamp: Date.now() }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { sender: 'user', text, timestamp: Date.now() }]);
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setTimeout(() => {
        addBotMessage('Hola 👋 ¿En qué podemos ayudarte?', [
          { label: 'Equipos agroindustriales (TECNAL)', value: 'tecnal' },
          { label: 'Bombas y válvulas (KSB)', value: 'ksb' },
          { label: 'Otra consulta', value: 'otra' }
        ]);
        setStep('welcome');
      }, 300);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleReset = () => {
    setMessages([]);
    setStep(null);
    setFormData({ nombre: '', empresa: '', email: '', consulta: '' });
    setInputValue('');
    setSending(false);
    setTimeout(() => {
      addBotMessage('Hola 👋 ¿En qué podemos ayudarte?', [
        { label: 'Equipos agroindustriales (TECNAL)', value: 'tecnal' },
        { label: 'Bombas y válvulas (KSB)', value: 'ksb' },
        { label: 'Otra consulta', value: 'otra' }
      ]);
      setStep('welcome');
    }, 200);
  };

  const handleOption = (value) => {
    if (step === 'welcome') {
      if (value === 'tecnal') {
        addUserMessage('Equipos agroindustriales (TECNAL)');
        setTimeout(() => {
          addBotMessage('¿Querés explorar nuestros equipos o preferís que te contactemos?', [
            { label: 'Ver equipos', value: 'nav-tecnal' },
            { label: 'Que me contacten', value: 'contacto' }
          ]);
          setStep('tecnal-ksb');
        }, 400);
      } else if (value === 'ksb') {
        addUserMessage('Bombas y válvulas (KSB)');
        setTimeout(() => {
          addBotMessage('¿Querés explorar nuestros productos o preferís que te contactemos?', [
            { label: 'Ver productos', value: 'nav-ksb' },
            { label: 'Que me contacten', value: 'contacto' }
          ]);
          setStep('tecnal-ksb');
        }, 400);
      } else {
        addUserMessage('Otra consulta');
        setTimeout(() => {
          showContactChannel();
        }, 400);
      }
    } else if (step === 'tecnal-ksb') {
      if (value === 'nav-tecnal') {
        addUserMessage('Ver equipos');
        navigate('/tecnal');
        setIsOpen(false);
      } else if (value === 'nav-ksb') {
        addUserMessage('Ver productos');
        navigate('/ksb');
        setIsOpen(false);
      } else if (value === 'contacto') {
        addUserMessage('Que me contacten');
        setTimeout(() => {
          showContactChannel();
        }, 400);
      }
    } else if (step === 'canal') {
      if (value === 'whatsapp') {
        addUserMessage('Por WhatsApp');
        window.open(WHATSAPP_LINK, '_blank');
      } else if (value === 'email-flow') {
        addUserMessage('Por email');
        setTimeout(() => {
          addBotMessage('¿Cuál es tu nombre?');
          setStep('nombre');
        }, 400);
      }
    }
  };

  const showContactChannel = () => {
    addBotMessage('¿Cómo preferís que te contactemos?', [
      { label: '📧 Por email', value: 'email-flow' },
      { label: '💬 Por WhatsApp', value: 'whatsapp' }
    ]);
    setStep('canal');
  };

  const handleInputSubmit = async () => {
    const val = inputValue.trim();
    if (!val) return;

    addUserMessage(val);
    setInputValue('');

    if (step === 'nombre') {
      setFormData(prev => ({ ...prev, nombre: val }));
      setTimeout(() => {
        addBotMessage('¿De qué empresa sos?');
        setStep('empresa');
      }, 400);
    } else if (step === 'empresa') {
      setFormData(prev => ({ ...prev, empresa: val }));
      setTimeout(() => {
        addBotMessage('¿Cuál es tu email?');
        setStep('email');
      }, 400);
    } else if (step === 'email') {
      setFormData(prev => ({ ...prev, email: val }));
      setTimeout(() => {
        addBotMessage('¿Cuál es tu consulta?');
        setStep('consulta');
      }, 400);
    } else if (step === 'consulta') {
      const data = { ...formData, consulta: val };
      setFormData(data);
      setSending(true);
      setStep('sending');

      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${apiBase}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });

        if (res.ok) {
          setTimeout(() => {
            addBotMessage(`¡Gracias ${data.nombre}! Te contactamos a la brevedad 🙌`);
            setStep('done');
            setSending(false);
          }, 500);
        } else {
          throw new Error('Error en el envío');
        }
      } catch {
        setTimeout(() => {
          addBotMessage('Hubo un error al enviar. Podés contactarnos por WhatsApp o intentar de nuevo.');
          setStep('done');
          setSending(false);
        }, 500);
      }
    }
  };

  const showInput = step === 'nombre' || step === 'empresa' || step === 'email' || step === 'consulta';

  const getPlaceholder = () => {
    if (step === 'nombre') return 'Tu nombre...';
    if (step === 'empresa') return 'Nombre de la empresa...';
    if (step === 'email') return 'tu@email.com';
    if (step === 'consulta') return 'Escribí tu consulta...';
    return '';
  };

  return (
    <>
      {/* Chat panel */}
      <div className={`floating-chat-panel${isOpen ? ' floating-chat-panel--open' : ''}`}>
        {/* Header */}
        <div className="floating-chat-header">
          <div className="floating-chat-header__info">
            <span className="floating-chat-header__title">Meyle SRL</span>
            <span className="floating-chat-header__status">En línea</span>
          </div>
          <div className="floating-chat-header__actions">
            {step !== 'welcome' && step !== null && (
              <button className="floating-chat-header__btn" onClick={handleReset} aria-label="Reiniciar">
                ↺
              </button>
            )}
            <button className="floating-chat-header__btn" onClick={handleClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="floating-chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`floating-chat-msg floating-chat-msg--${msg.sender}`}>
              <div className="floating-chat-msg__bubble">
                {msg.text}
              </div>
              {msg.options && (
                <div className="floating-chat-options">
                  {msg.options.map((opt, j) => (
                    <button
                      key={j}
                      className="floating-chat-option"
                      onClick={() => handleOption(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="floating-chat-msg floating-chat-msg--bot">
              <div className="floating-chat-msg__bubble floating-chat-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {showInput && (
          <div className="floating-chat-input">
            <input
              ref={inputRef}
              type={step === 'email' ? 'email' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInputSubmit()}
              placeholder={getPlaceholder()}
              className="floating-chat-input__field"
            />
            <button
              className="floating-chat-input__send"
              onClick={handleInputSubmit}
              disabled={!inputValue.trim()}
              aria-label="Enviar"
            >
              <Send size={18} />
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="floating-chat-input">
            <button className="floating-chat-restart" onClick={handleReset}>
              Iniciar nueva consulta
            </button>
          </div>
        )}
      </div>

      {/* Floating button */}
      <button
        className={`floating-chat-btn${isOpen ? ' floating-chat-btn--hidden' : ''}`}
        onClick={handleOpen}
        aria-label="Abrir chat"
      >
        <MessageCircle size={26} />
      </button>
    </>
  );
};

export default FloatingChat;
